/**
 * Fonctions de formatage réutilisables
 */

/**
 * Formate un montant en euros avec séparateurs français
 * @param montant Le montant à formater
 * @param showCurrency Afficher le symbole € (défaut: true)
 * @returns Le montant formaté (ex: "1 234,56 €")
 */
export function formatMontant(montant: number | null | undefined, showCurrency = true): string {
  if (montant === null || montant === undefined) return showCurrency ? '0 €' : '0';

  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(montant);

  return showCurrency ? `${formatted} €` : formatted;
}

/**
 * Formate un montant compact (sans décimales si entier)
 * @param montant Le montant à formater
 * @returns Le montant formaté (ex: "1 234 €" ou "1 234,50 €")
 */
export function formatMontantCompact(montant: number | null | undefined): string {
  if (montant === null || montant === undefined) return '0 €';

  const isInteger = Number.isInteger(montant);

  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(montant);

  return `${formatted} €`;
}

/**
 * Formate une date ISO en format français court
 * @param dateStr Date au format ISO (YYYY-MM-DD) ou objet Date
 * @returns Date formatée (ex: "15/01/2026")
 */
export function formatDate(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '-';

  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return '-';
  }
}

/**
 * Formate une date en format long français
 * @param dateStr Date au format ISO ou objet Date
 * @returns Date formatée (ex: "15 janvier 2026")
 */
export function formatDateLong(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '-';

  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return '-';
  }
}

/**
 * Formate un pourcentage
 * @param value La valeur (0-100 ou 0-1)
 * @param isDecimal Si true, la valeur est entre 0 et 1
 * @returns Le pourcentage formaté (ex: "45,5 %")
 */
export function formatPourcentage(value: number | null | undefined, isDecimal = false): string {
  if (value === null || value === undefined) return '0 %';

  const pct = isDecimal ? value * 100 : value;

  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(pct) + ' %';
}
